import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Message, MessageList } from "../../types/MessageListType";

// Define the type for your state
interface MessageOptionState {
  selectMessage: boolean;
  message_list: MessageList;
  showModal: boolean;
  modalName:
    | "clear_chat"
    | "delete_chat"
    | "report_user"
    | "block_user"
    | "remove_member"
    | "exit_from_group"
    | "delete_group"
    | "delete_conversation"
    | "notification"
    | "";
  title: string;
  description: string;
  totalPages: number;
  currentPage: string;
  isMessageLoading: boolean;
  isMoreMessageLoading: boolean;
  delete_message: boolean;
  delete_only_from_me: boolean;
  delete_from_every_one: boolean;
  show_forward_message_modal: boolean;
  show_send_location_modal: boolean;
  show_select_about_modal: boolean;
  forward_message: boolean;
  show_pin_message_modal: boolean;
  messageListAtTop: boolean;
  show_all_star_messages: boolean;
  pinned_duration?: "1_day" | "7_days" | "1_month" | "lifetime";
  /** page = tip history; window = around-jump / keyset window */
  historyMode: "page" | "window";
  has_more_older: boolean;
  has_more_newer: boolean;
  /** True when the newest bubble is in view (compose list at tip). */
  message_list_at_bottom: boolean;
  /** Live missed messages while scrolled up / in deep history (socket receive only). */
  missed_below_count: number;
}

// Define the initial state using the MessageOptionState interface
const initialState: MessageOptionState = {
  selectMessage: false,
  message_list: {
    Records: [],
    Pagination: {
      total_pages: 0,
      total_records: 0,
      current_page: 0,
      records_per_page: 0,
    },
  },
  showModal: false,
  modalName: "",
  title: "",
  description: "",
  totalPages: 0,
  currentPage: "1",
  isMessageLoading: false,
  isMoreMessageLoading: false,
  delete_message: false,
  delete_only_from_me: false,
  forward_message: false,
  show_forward_message_modal: false,
  show_pin_message_modal: false,
  show_select_about_modal: false,
  delete_from_every_one: false,
  messageListAtTop: false,
  show_all_star_messages: false,
  show_send_location_modal: false,
  pinned_duration: "1_day",
  historyMode: "page",
  has_more_older: false,
  has_more_newer: false,
  message_list_at_bottom: true,
  missed_below_count: 0,
};

// Create the slice with proper TypeScript types
const MessageOptionsSlice = createSlice({
  name: "MessageOptions",
  initialState,
  reducers: {
    // Updates the state with partial payload
    updateMessageOptions(
      state,
      action: PayloadAction<Partial<MessageOptionState>>,
    ) {
      return { ...state, ...action.payload };
    },

    // Append a message object to the list
    addMessage(state, action: PayloadAction<Message>) {
      state.message_list.Records.push(action.payload);
    },

    // Remove a message object from the list by message_id
    removeMessage(state, action: PayloadAction<number>) {
      state.message_list.Records = state.message_list.Records.filter(
        (message) => message.message_id !== action.payload,
      );
      // Last unchecked → exit select / forward / delete mode
      if (state.message_list.Records.length === 0) {
        state.selectMessage = false;
        state.forward_message = false;
        state.delete_message = false;
        state.delete_only_from_me = false;
        state.delete_from_every_one = false;
      }
    },

    // Remove all selected message
    removeSelectedMessage(state) {
      state.message_list.Records = [];
    },

    incrementMissedBelow(state) {
      state.missed_below_count = Math.min(999, (state.missed_below_count || 0) + 1);
    },

    resetMissedBelow(state) {
      state.missed_below_count = 0;
    },
  },
});

// Export the reducer and actions
export default MessageOptionsSlice.reducer;
export const {
  updateMessageOptions,
  addMessage,
  removeSelectedMessage,
  removeMessage,
  incrementMissedBelow,
  resetMissedBelow,
} = MessageOptionsSlice.actions;
